import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  TablePagination,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  Divider,
  Badge,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { allContactsData, getContactStats } from './DataSet';

// Enhanced Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '24px',
  border: 'none',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
  overflow: 'hidden',
  width: '100%',
  maxWidth: '100%',
  backdropFilter: 'blur(10px)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  borderBottom: '1px solid #e2e8f0',
  padding: '24px 32px',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '20px 24px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 20px'
  }
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    borderColor: '#3b82f6'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px'
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '20px',
    backgroundColor: '#ffffff',
    border: '2px solid transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: '0.95rem',
    '&:hover': {
      border: '2px solid #e2e8f0',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
    },
    '&.Mui-focused': {
      border: '2px solid #3b82f6',
      boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
      backgroundColor: '#ffffff'
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  }
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '16px',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    width: 44,
    height: 44
  },
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40
  },
  '&:hover': {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
    color: '#ffffff',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover::before': {
    left: '100%',
  }
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
  border: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
  }
}));

// Updated Gmail-style Table Container
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '0',
  border: 'none',
  background: '#ffffff',
  overflow: 'auto',
  maxWidth: '100%',
  height: '65vh', // Fixed height for Gmail-style scrolling
  minHeight: '500px',
  position: 'relative',
  
  // Gmail-style scrollbar
  '&::-webkit-scrollbar': {
    width: 12,
    height: 12,
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f5f9',
    borderRadius: 8,
    margin: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(145deg, #94a3b8 0%, #64748b 100%)',
    borderRadius: 8,
    border: '2px solid #f1f5f9',
    '&:hover': {
      background: 'linear-gradient(145deg, #64748b 0%, #475569 100%)',
    },
    '&:active': {
      background: 'linear-gradient(145deg, #475569 0%, #334155 100%)',
    }
  },
  '&::-webkit-scrollbar-corner': {
    background: '#f1f5f9',
  },
  
  // Firefox scrollbar
  scrollbarWidth: 'thin',
  scrollbarColor: '#94a3b8 #f1f5f9',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  '& .MuiTableCell-head': {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    borderBottom: 'none',
    padding: '12px 16px', // Reduced padding for more compact look
    background: 'transparent',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      padding: '10px 14px',
      fontSize: '0.7rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '8px 12px',
      fontSize: '0.65rem',
    }
  }
}));

// More compact table rows for Gmail-style
const StyledTableRow = styled(TableRow)(({ theme, isEven }) => ({
  background: isEven ? '#ffffff' : '#f8fafc',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  minHeight: '48px', // Reduced row height
  '&:hover': {
    background: 'linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)',
    transform: 'translateX(2px)',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
    '& .row-actions': {
      opacity: 1,
      transform: 'translateX(0)',
    }
  },
  '& .row-actions': {
    opacity: 0,
    transform: 'translateX(4px)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    [theme.breakpoints.down('md')]: {
      opacity: 1,
      transform: 'translateX(0)',
    }
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #f1f5f9',
    padding: '8px 16px', // Much smaller padding
    verticalAlign: 'middle',
    [theme.breakpoints.down('md')]: {
      padding: '6px 14px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '4px 12px',
    }
  }
}));

const StatusChip = styled(Chip)(({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'Active': return { 
        bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(16, 185, 129, 0.25)'
      };
      case 'Pending': return { 
        bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(245, 158, 11, 0.25)'
      };
      case 'Inactive': return { 
        bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(239, 68, 68, 0.25)'
      };
      case 'Customer': return { 
        bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(16, 185, 129, 0.25)'
      };
      case 'Prospect': return { 
        bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(59, 130, 246, 0.25)'
      };
      case 'Lead': return { 
        bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(245, 158, 11, 0.25)'
      };
      default: return { 
        bg: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', 
        color: '#ffffff',
        shadow: '0 2px 6px rgba(107, 114, 128, 0.25)'
      };
    }
  };
  
  const colors = getStatusColor();
  
  return {
    background: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: '0.7rem', // Smaller font
    height: '24px', // Smaller chip height
    borderRadius: '12px',
    boxShadow: colors.shadow,
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `${colors.shadow}, 0 1px 3px rgba(0,0,0,0.1)`
    },
    '& .MuiChip-label': {
      padding: '0 10px',
    }
  };
});

export const Contacts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100); // Show all 100 records by default
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        // Simulate API call with loading delay
        setTimeout(() => {
          setContacts(allContactsData);
          setStats(getContactStats());
          setLoading(false);
        }, 1500);
      } catch (err) {
        setError('Failed to load contacts');
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  // Filter contacts based on search term
  const filteredContacts = contacts.filter(contact =>
    Object.values(contact).some(value =>
      value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // For Gmail-style, we'll show all filtered contacts in the scrollable area
  const displayedContacts = filteredContacts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, contact) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedContact(contact);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContact(null);
  };

  const handleView = () => {
    console.log('View contact:', selectedContact);
    handleMenuClose();
  };

  const handleEdit = () => {
    console.log('Edit contact:', selectedContact);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedContact && window.confirm(`Are you sure you want to delete ${selectedContact.contactName}?`)) {
      const updatedContacts = contacts.filter(contact => contact.id !== selectedContact.id);
      setContacts(updatedContacts);
      setStats(getContactStats());
    }
    handleMenuClose();
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setContacts(allContactsData);
      setStats(getContactStats());
      setLoading(false);
    }, 800);
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase();
  };

  const getRowColor = (contact) => {
    switch (contact.status) {
      case 'Active':
        return 'rgba(16, 185, 129, 0.02)';
      case 'Inactive':
        return 'rgba(239, 68, 68, 0.02)';
      case 'Pending':
        return 'rgba(245, 158, 11, 0.02)';
      default:
        return 'inherit';
    }
  };

  // Define columns based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['contactName', 'contactType'];
    } else if (isTablet) {
      return ['contactName', 'email', 'phone', 'contactType'];
    }
    return ['contactName', 'jobTitle', 'email', 'phone', 'company', 'contactType'];
  };

  const visibleColumns = getVisibleColumns();

  if (loading) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: '#3b82f6', 
              mb: 3,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
          />
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1, fontWeight: 600 }}>
            Loading Contacts...
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Fetching your contact database
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '16px',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.15)' 
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      minHeight: '100vh',
      backgroundColor: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden'
    }}>
      <Fade in={true} timeout={800}>
        <Box>
          {/* Stats Cards */}
          <Box sx={{ mb: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' } }}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PeopleIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                    {stats.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Total Contacts
                  </Typography>
                </Box>
              </Box>
            </StatsCard>

            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrendingUpIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                    {stats.active || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Active
                  </Typography>
                </Box>
              </Box>
            </StatsCard>

            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AttachMoneyIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                    {stats.customers || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Customers
                  </Typography>
                </Box>
              </Box>
            </StatsCard>

            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ScheduleIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                    {stats.needingFollowUp || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Follow-up
                  </Typography>
                </Box>
              </Box>
            </StatsCard>
          </Box>

          <StyledCard>
            {/* Header Section */}
            <HeaderSection>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', lg: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', lg: 'center' },
                gap: { xs: 3, lg: 0 },
                mb: 4 
              }}>
                <Box>
                  <Typography variant={isMobile ? "h4" : "h3"} sx={{ 
                    fontWeight: 800, 
                    color: '#1e293b',
                    mb: 1,
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Contact Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                    {filteredContacts.length} contacts • Gmail-style view • Last updated just now
                  </Typography>
                </Box>
                
                <Stack direction="row" spacing={2} alignItems="center">
                  <PrimaryButton
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                  >
                    Add Contact
                  </PrimaryButton>
                  
                  <Tooltip title="Refresh Data">
                    <ActionButton onClick={handleRefresh}>
                      <RefreshIcon sx={{ fontSize: isMobile ? 20 : 22 }} />
                    </ActionButton>
                  </Tooltip>
                  
                  {!isMobile && (
                    <>
                      <Tooltip title="Filter Contacts">
                        <ActionButton>
                          <FilterIcon sx={{ fontSize: 22 }} />
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title="Export Data">
                        <ActionButton>
                          <DownloadIcon sx={{ fontSize: 22 }} />
                        </ActionButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Box>

              {/* Search Bar */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 3, 
                alignItems: { xs: 'stretch', sm: 'center' } 
              }}>
                <SearchField
                  placeholder="Search contacts by name, company, email, or phone..."
                  variant="outlined"
                  size={isMobile ? "medium" : "large"}
                  fullWidth={isMobile}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#64748b', fontSize: isMobile ? 20 : 24 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: { sm: 450, md: 550 } }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Badge 
                    badgeContent={filteredContacts.length} 
                    color="primary" 
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        fontWeight: 600
                      }
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#64748b', 
                        fontWeight: 600,
                        minWidth: 'max-content',
                        textAlign: { xs: 'center', sm: 'left' }
                      }}
                    >
                      {searchTerm ? `Filtered Results` : `All Contacts`}
                    </Typography>
                  </Badge>
                </Box>
              </Box>
            </HeaderSection>

            {/* Gmail-style Table Section with Fixed Height and Scrollbar */}
            <StyledTableContainer>
              <Table stickyHeader size="small">
                <StyledTableHead>
                  <TableRow>
                    {visibleColumns.includes('contactName') && (
                      <TableCell>Contact Name</TableCell>
                    )}
                    {visibleColumns.includes('jobTitle') && (
                      <TableCell>Job Title</TableCell>
                    )}
                    {visibleColumns.includes('email') && (
                      <TableCell>Email</TableCell>
                    )}
                    {visibleColumns.includes('phone') && (
                      <TableCell>Phone</TableCell>
                    )}
                    {visibleColumns.includes('company') && (
                      <TableCell>Company</TableCell>
                    )}
                    {visibleColumns.includes('contactType') && (
                      <TableCell>Type</TableCell>
                    )}
                    <TableCell align="center" sx={{ width: '80px' }}>Actions</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {displayedContacts.map((contact, index) => (
                    <StyledTableRow 
                      key={contact.id}
                      isEven={index % 2 === 0}
                      sx={{ backgroundColor: getRowColor(contact) }}
                    >
                      {/* Contact Name with Smaller Avatar */}
                      {visibleColumns.includes('contactName') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar 
                              sx={{ 
                                width: isMobile ? 28 : 32, 
                                height: isMobile ? 28 : 32,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontWeight: 700,
                                fontSize: isMobile ? '0.7rem' : '0.8rem',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)'
                              }}
                            >
                              {getInitials(contact.contactName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: '#1e293b', 
                                  fontSize: '0.85rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isMobile ? '120px' : '180px'
                                }}
                              >
                                {contact.contactName}
                              </Typography>
                              {!isMobile && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#64748b', 
                                    fontSize: '0.7rem',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '180px'
                                  }}
                                >
                                  {contact.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      )}

                      {/* Job Title */}
                      {visibleColumns.includes('jobTitle') && (
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#64748b', 
                              fontSize: '0.8rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '140px'
                            }}
                          >
                            {contact.jobTitle}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Email */}
                      {visibleColumns.includes('email') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)',
                              flexShrink: 0
                            }}>
                              <EmailIcon sx={{ fontSize: 14, color: '#ffffff' }} />
                            </Box>
                            {!isMobile && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#3b82f6',
                                  fontSize: '0.8rem',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '160px',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {contact.email}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}

                      {/* Phone */}
                      {visibleColumns.includes('phone') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)',
                              flexShrink: 0
                            }}>
                              <PhoneIcon sx={{ fontSize: 14, color: '#ffffff' }} />
                            </Box>
                            {!isMobile && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.8rem',
                                  fontWeight: 500, 
                                  color: '#1e293b',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {contact.phone}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}

                      {/* Company */}
                      {visibleColumns.includes('company') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 6px rgba(139, 92, 246, 0.25)',
                              flexShrink: 0
                            }}>
                              <BusinessIcon sx={{ fontSize: 14, color: '#ffffff' }} />
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600, 
                                color: '#1e293b',
                                fontSize: '0.8rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '120px'
                              }}
                            >
                              {contact.company}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}

                      {/* Contact Type */}
                      {visibleColumns.includes('contactType') && (
                        <TableCell>
                          <StatusChip 
                            label={contact.contactType}
                            size="small"
                            status={contact.contactType}
                          />
                        </TableCell>
                      )}

                      {/* Actions */}
                      <TableCell align="center">
                        <Tooltip title="More Actions">
                          <IconButton
                            className="row-actions"
                            size="small"
                            onClick={(e) => handleMenuOpen(e, contact)}
                            sx={{
                              width: 28,
                              height: 28,
                              background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                                '& .MuiSvgIcon-root': {
                                  color: '#ffffff'
                                }
                              }
                            }}
                          >
                            <MoreVertIcon sx={{ fontSize: 16, color: '#64748b' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>

            {/* Simplified Pagination - Only show when needed */}
            {filteredContacts.length > 100 && (
              <Box sx={{ 
                p: 2, 
                borderTop: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <TablePagination
                  rowsPerPageOptions={[50, 100, 200]}
                  component="div"
                  count={filteredContacts.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: '#64748b'
                    },
                    '& .MuiTablePagination-select': {
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.85rem'
                    }
                  }}
                />
              </Box>
            )}

            {/* Enhanced Actions Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { 
                  minWidth: isMobile ? 140 : 180,
                  borderRadius: '12px',
                  boxShadow: '0 12px 28px -8px rgba(0, 0, 0, 0.2)',
                  border: '1px solid #e2e8f0',
                  overflow: 'visible',
                  mt: 0.5
                }
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleView} sx={{ py: 1.5, px: 2, borderRadius: '6px', mx: 1, mb: 0.5, fontSize: '0.85rem' }}>
                <ViewIcon sx={{ mr: 1.5, fontSize: 18, color: '#3b82f6' }} />
                View Details
              </MenuItem>
              <MenuItem onClick={handleEdit} sx={{ py: 1.5, px: 2, borderRadius: '6px', mx: 1, mb: 0.5, fontSize: '0.85rem' }}>
                <EditIcon sx={{ mr: 1.5, fontSize: 18, color: '#10b981' }} />
                Edit Contact
              </MenuItem>
              <Divider sx={{ mx: 1 }} />
              <MenuItem 
                onClick={handleDelete} 
                sx={{ 
                  py: 1.5, 
                  px: 2, 
                  borderRadius: '6px', 
                  mx: 1, 
                  mt: 0.5,
                  fontSize: '0.85rem',
                  '&:hover': { backgroundColor: '#fef2f2' } 
                }}
              >
                <DeleteIcon sx={{ mr: 1.5, fontSize: 18, color: '#ef4444' }} />
                <Typography variant="inherit" color="#ef4444">Delete Contact</Typography>
              </MenuItem>
            </Menu>
          </StyledCard>
        </Box>
      </Fade>
    </Box>
  );
};

export default Contacts;